import Link from '@/components/UI/Link';
import { _isNil, _notNil } from '@/littledash';
import type { State } from '@/model/State.model';
import * as Auth from '@/support/auth';
import { web as webRoute } from '@/support/route';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RiGroupLine, RiLogoutBoxRLine, RiQuestionLine, RiTeamLine, RiUser3Line } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import styles from './UserMenu.module.scss';

/**
 * User avatar in the sidebar that opens a popover with account actions,
 * help links, and logout.
 */
const UserMenu: FC = () => {
  const currentUser = useSelector((state: State) => state.user.currentUser);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const initials = getInitials(currentUser?.name);
  const isTeamOwner = Auth.isCurrentTeamOwner();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (_notNil(menuRef.current) && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    };

    window.addEventListener('keydown', handleEsc);
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, close]);

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    dispatch({ type: 'TEAM_SET_TEAM', team: null });
    dispatch({ type: 'USER_SET_CURRENT_USER', currentUser: null });
  };

  return (
    <div className="tc mv2" data-testid="user-menu" ref={menuRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        data-testid="user-menu-trigger"
      >
        <span className={styles.triggerText}>{initials}</span>
      </button>

      {open && (
        <div className={styles.menu} data-testid="user-menu-popover">
          <div className={styles.header}>
            <div className={styles.headerAvatar}>
              <span className={styles.headerAvatarText}>{initials}</span>
            </div>
            <div className={styles.headerInfo}>
              <span className={styles.headerName} data-testid="user-menu-name">
                {currentUser?.name}
              </span>
              <span className={styles.headerEmail} data-testid="user-menu-email">
                {currentUser?.email}
              </span>
            </div>
          </div>

          <div className={styles.section}>
            <Link to={webRoute('account')} onClick={close} className={styles.menuItem} data-testid="user-menu-account">
              <RiUser3Line className={styles.icon} />
              Account
            </Link>
            {isTeamOwner && (
              <Link
                to={webRoute('team.manage')}
                onClick={close}
                className={styles.menuItem}
                data-testid="user-menu-manage-team"
              >
                <RiGroupLine className={styles.icon} />
                Manage team
              </Link>
            )}
          </div>

          <div className={styles.section}>
            <span className={styles.subHeader}>Resources</span>
            <Link
              to={webRoute('help')}
              openTab
              className={styles.menuItemSecondary}
              data-testid="user-menu-help-center"
            >
              <RiQuestionLine className={styles.icon} />
              Help center
            </Link>
            <Link
              to={webRoute('community')}
              openTab
              className={styles.menuItemSecondary}
              data-testid="user-menu-community"
            >
              <RiTeamLine className={styles.icon} />
              Ask the community
            </Link>
          </div>

          <div className={`${styles.section} ${styles.sectionLast}`}>
            <button type="button" className={styles.menuItem} onClick={handleLogout} data-testid="user-menu-logout">
              <RiLogoutBoxRLine className={styles.iconDanger} />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const getInitials = (name?: string): string => {
  if (_isNil(name) || name.trim() === '') {
    return '-';
  }
  const matches = name.match(/\b\w/g);
  return `${matches?.shift() ?? ''}${matches?.pop() ?? ''}`.toUpperCase();
};

export default UserMenu;
